import React, { useEffect, useMemo, useRef, useState } from 'react';
import { tokenizeJson } from './promptModel';

export const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M2.5 4h11M6 4V2.5h4V4M4 4l.7 9.5h6.6L12 4M6.7 7v4M9.3 7v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M8 7.2v3.6M8 5.1v.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg width="18" height="24" viewBox="0 0 18 24" fill="none" aria-hidden="true">
    <path d="M9 3v17M3 14l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Dialog = ({ title, description, onClose, closeOnEscape = false, wide = false, children, footer }) => {
  useEffect(() => {
    if (!closeOnEscape) {
      return undefined;
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeOnEscape, onClose]);

  return (
    <div className="pe-overlay">
      <div className={wide ? 'pe-dialog is-wide' : 'pe-dialog'} role="dialog" aria-modal="true" aria-label={title}>
        <div className="pe-dialog-head">
          <div>
            <h2>{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <button type="button" className="pe-icon-button" aria-label="Close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="pe-dialog-body">{children}</div>
        {footer && <div className="pe-dialog-foot">{footer}</div>}
      </div>
    </div>
  );
};

/** A textarea over a coloured copy of its text. The copy never changes the text. */
const JsonCodeEditor = ({ value, onChange, label }) => {
  const layer = useRef(null);
  const tokens = useMemo(() => tokenizeJson(value), [value]);

  const syncScroll = (event) => {
    layer.current.scrollTop = event.target.scrollTop;
    layer.current.scrollLeft = event.target.scrollLeft;
  };

  return (
    <div className="pe-code">
      <pre ref={layer} className="pe-code-layer" aria-hidden="true">
        {tokens.map((token, index) => (
          // Tokens are recreated on every edit and never reorder.
          // eslint-disable-next-line react/no-array-index-key
          <span key={index} className={`pe-t-${token.type}`}>{token.text}</span>
        ))}
        {'\n'}
      </pre>
      <textarea
        className="pe-code-input"
        aria-label={label}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onScroll={syncScroll}
      />
    </div>
  );
};

/**
 * Edits one JSON value. `parse` turns the text into `{ value }` or `{ error }`, and nothing is
 * saved until it parses.
 */
export const JsonDialog = ({ title, description, label, initialText, parse, onSave, onDelete, onClose }) => {
  const [text, setText] = useState(initialText);
  const [error, setError] = useState(null);

  const save = () => {
    const result = parse(text);
    if (result.error) {
      setError(result.error);
      return;
    }
    onSave(result.value);
  };

  return (
    <Dialog
      wide
      title={title}
      description={description}
      onClose={onClose}
      footer={(
        <>
          {onDelete && (
            <button type="button" className="pe-button is-quiet" aria-label="Delete function" onClick={onDelete}><TrashIcon /></button>
          )}
          {error && <p className="pe-dialog-error" role="alert">{error}</p>}
          <div className="pe-spacer" />
          <button type="button" className="pe-button" onClick={onClose}>Cancel</button>
          <button type="button" className="pe-button is-primary" onClick={save}>Save</button>
        </>
      )}
    >
      <p className="pe-field-label">{label}</p>
      <JsonCodeEditor label={label} value={text} onChange={(next) => { setText(next); setError(null); }} />
    </Dialog>
  );
};

/** The prompt at full size. Edits go straight to the draft. */
export const TextDialog = ({ title, value, onChange, onClose }) => (
  <Dialog
    wide
    closeOnEscape
    title={title}
    onClose={onClose}
    footer={(
      <>
        <p className="pe-muted">{value.length.toLocaleString()} characters</p>
        <div className="pe-spacer" />
        <button type="button" className="pe-button is-primary" onClick={onClose}>Done</button>
      </>
    )}
  >
    <textarea className="pe-textarea is-full" aria-label={title} value={value} onChange={(event) => onChange(event.target.value)} />
  </Dialog>
);

export const PublishDialog = ({ fromLabel, toLabel, isFirstVersion, busy, error, onPublish, onClose }) => {
  const [note, setNote] = useState('');
  const [setDefault, setSetDefault] = useState(true);

  return (
    <Dialog
      closeOnEscape
      title="Publish changes?"
      description="Create a new version of the prompt with your latest changes. Published versions stay as they are, and builds pinned to them keep working."
      onClose={onClose}
    >
      <div className="pe-publish-versions">
        <span className="pe-version-pill">{fromLabel}</span>
        <ArrowDownIcon />
        <span className="pe-version-pill">{toLabel}</span>
      </div>

      <label className="pe-field">
        <span className="pe-field-label">What changed and why</span>
        <input className="pe-input" maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
      </label>

      {error && <p className="pe-dialog-error" role="alert">{error}</p>}

      <div className="pe-publish-foot">
        <label className="pe-check">
          <input
            type="checkbox"
            checked={isFirstVersion || setDefault}
            disabled={isFirstVersion}
            onChange={(event) => setSetDefault(event.target.checked)}
          />
          <span>Set as default version</span>
          <span className="pe-info" tabIndex={0} role="note" aria-label="The default version is used when a request sends no version number. Builds pinned to a number are not affected.">
            <InfoIcon />
            <span className="pe-tooltip" role="tooltip">The default version is used when a request sends no version number. Builds pinned to a number are not affected.</span>
          </span>
        </label>
        <button type="button" className="pe-button is-primary is-large" disabled={busy} onClick={() => onPublish(note.trim(), isFirstVersion || setDefault)}>
          {busy ? 'Publishing…' : 'Publish'}
        </button>
      </div>
    </Dialog>
  );
};
